/* Response types */

import {ApiUtil} from './ApiUtil'
import {
  mapAdminProductResponse,
  mapAdminProductsResponse,
  mapAdminUserResponse,
  mapAdminUsersResponse
} from './response-mappers'
import type {AdminProductResponseItem, AdminUserResponseItem, FailedResponse} from './types'

interface AdminProductsSuccessResponse {
  succeeded: true
  payload: {
    products: AdminProductResponseItem[]
  }
}

export type AdminProductsResponse = AdminProductsSuccessResponse | FailedResponse

interface AdminUsersSuccessResponse {
  succeeded: true
  payload: {
    users: AdminUserResponseItem[]
  }
}

export type AdminUsersResponse = AdminUsersSuccessResponse | FailedResponse

interface ProductUpdateSuccessResponse {
  succeeded: true
  payload: {
    product: AdminProductResponseItem
  }
}

export type ProductUpdateResponse = ProductUpdateSuccessResponse | FailedResponse

/**
 * Payload (body) for user creation/update requests.
 */
interface UserRequestPayload {
  email: string
  useAuthorized: boolean
  isAdmin: boolean
}

interface UserUpdateSuccessResponse {
  succeeded: true
  payload: {
    user: AdminUserResponseItem
  }
}

export type UserUpdateResponse = UserUpdateSuccessResponse | FailedResponse

/* API methods */

/**
 * Get list of products for administrative purposes.
 * @param token authentication token.
 */
async function getProducts(token: string): Promise<AdminProductsResponse> {
  const apiDesc = 'query products for administration'
  const url = ApiUtil.getApiUrl('/admin/products')

  try {
    const response = await ApiUtil.doGetRequest(url, token)
    if (!response.ok) {
      return await ApiUtil.errorResponse(response, apiDesc)
    }

    const payload: unknown = await response.json()
    const parsedPayload = mapAdminProductsResponse(payload)
    return {
      succeeded: true,
      payload: {
        products: parsedPayload
      }
    }
  } catch (e) {
    return ApiUtil.unknownExceptionResponse(e, url, apiDesc)
  }
}

/**
 * Get a list of users for administrative purposes.
 * @param token authentication token.
 */
async function getUsers(token: string): Promise<AdminUsersResponse> {
  const apiDesc = 'query users for administration'
  const url = ApiUtil.getApiUrl('/admin/authorized-users')

  try {
    const response = await ApiUtil.doGetRequest(url, token)
    if (!response.ok) {
      return await ApiUtil.errorResponse(response, apiDesc)
    }

    const payload: unknown = await response.json()
    const parsedPayload = mapAdminUsersResponse(payload)
    return {
      succeeded: true,
      payload: {
        users: parsedPayload
      }
    }
  } catch (e) {
    return ApiUtil.unknownExceptionResponse(e, url, apiDesc)
  }
}

/**
 * Get a list of users that is not currently authorized to use the system for administrative purposes.
 * @param token authentication token.
 */
async function getNotAuthorizedUsers(token: string): Promise<AdminUsersResponse> {
  const apiDesc = 'query not authorized users for administration'
  const url = ApiUtil.getApiUrl('/admin/not-authorized-users')

  try {
    const response = await ApiUtil.doGetRequest(url, token)
    if (!response.ok) {
      return await ApiUtil.errorResponse(response, apiDesc)
    }

    const payload: unknown = await response.json()
    const parsedPayload = mapAdminUsersResponse(payload)
    return {
      succeeded: true,
      payload: {
        users: parsedPayload
      }
    }
  } catch (e) {
    return ApiUtil.unknownExceptionResponse(e, url, apiDesc)
  }
}

/**
 * Update product information.
 * @param id Product's ID.
 * @param input request input values.
 * @param token authentication token.
 */
async function updateProduct(
  id: string,
  input: {
    priceCents: number | null
  }, token: string
): Promise<ProductUpdateResponse> {
  const apiDesc = 'update product request'
  const url = ApiUtil.getApiUrl(`/admin/products/${id}`)
  const body = {...input}

  try {
    const response = await ApiUtil.doPutRequest(url, body, token)
    if (!response.ok) {
      return await ApiUtil.errorResponse(response, apiDesc)
    }
    const payload: unknown = await response.json()
    const parsedPayload = mapAdminProductResponse(payload)
    return {
      succeeded: true,
      payload: {
        product: parsedPayload
      }
    }
  } catch (e) {
    return ApiUtil.unknownExceptionResponse(e, url, apiDesc)
  }
}

/**
 * Create a new user.
 * @param input request input.
 * @param token authentication token.
 */
async function createUser(
  input: UserRequestPayload, token: string): Promise<UserUpdateResponse> {
  const apiDesc = 'update user request'
  const url = ApiUtil.getApiUrl('/admin/users')
  const body = {...input}

  return await doUserRequest(async () => await ApiUtil.doPostRequest(url, body, token), apiDesc, url)
}

/**
 * Update user's permission.
 * @param id existing user's ID.
 * @param input request input.
 * @param token authentication token.
 */
async function updateUserPermissions(id: string,
                                     input: UserRequestPayload,
                                     token: string
): Promise<UserUpdateResponse> {
  const apiDesc = 'update user request'
  const url = ApiUtil.getApiUrl(`/admin/users/${id}`)
  const body = {...input}

  return await doUserRequest(async () => await ApiUtil.doPutRequest(url, body, token), apiDesc, url)
}

/**
 * Backend API for the admin-section.
 */
export const AdminAPI = {
  createUser,
  getProducts,
  getUsers,
  getNotAuthorizedUsers,
  updateProduct,
  updateUserPermissions
}

export type AdminApiType = typeof AdminAPI

/* Helper Functions */

async function doUserRequest(requestFunc: () => Promise<Response>,
                             apiDesc: string,
                             url: string): Promise<UserUpdateResponse> {
  try {
    const response = await requestFunc()
    if (!response.ok) {
      return await ApiUtil.errorResponse(response, apiDesc)
    }
    const payload: unknown = await response.json()
    const parsedPayload = mapAdminUserResponse(payload)
    return {
      succeeded: true,
      payload: {
        user: parsedPayload
      }
    }
  } catch (e) {
    return ApiUtil.unknownExceptionResponse(e, url, apiDesc)
  }
}
